import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarOutlined, CaretDownOutlined } from '@ant-design/icons';
import CommonTable from '../../components/commonTable';
import { ClientDetailRoute } from '../../routes/routepath';
import './followUpsTable.scss';

const TAG_COLORS = {
  'Follow Up':       { bg: '#f5a623', text: '#fff' },
  'Not Pick':        { bg: '#2c3e6b', text: '#fff' },
  'Converted':       { bg: '#27ae60', text: '#fff' },
  'Renewal':         { bg: '#e91e8c', text: '#fff' },
  'Incoming':        { bg: '#3d8fd4', text: '#fff' },
  'Interested':      { bg: '#9b59b6', text: '#fff' },
  'Hot':             { bg: '#f5a623', text: '#fff' },
  'Visited GC':      { bg: '#8b4ac5', text: '#fff' },
  'Int. For Visit':  { bg: '#b07030', text: '#fff' },
  'Walk-in':         { bg: '#1a4b8c', text: '#fff' },
  'Client Referral': { bg: '#e91e8c', text: '#fff' },
  'default':         { bg: '#4a5568', text: '#fff' },
};

const FILTERS = [
  { key: 'dueToday',  label: 'Due Today',  count: 496  },
  { key: 'upcoming',  label: 'Upcoming',   count: 2602 },
  { key: 'overdue',   label: 'Overdue',    count: 1589, variant: 'red' },
  { key: 'all',       label: 'All',        count: 1013 },
];

const Tag = ({ label, color }) => (
  <div className="fu-badge" style={{ background: color.bg, color: color.text }}>
    {label}
  </div>
);

// Combine every taggable field a lead can carry (source, stage, custom groups)
// into one ordered list of {label, color} pills for the Details column.
const buildTags = (record) => {
  const tags = [];
  if (record.leadSource && record.leadSource !== '—' && record.leadSource !== '-') {
    tags.push({ label: record.leadSource, color: TAG_COLORS[record.leadSource] || TAG_COLORS.default });
  }
  if (record.leadStage && record.leadStage !== '—' && record.leadStage !== '-') {
    tags.push({ label: record.leadStage, color: TAG_COLORS[record.leadStage] || TAG_COLORS.default });
  }
  if (Array.isArray(record.groups)) {
    record.groups.forEach((g) => {
      if (g?.name) tags.push({ label: g.name, color: { bg: g.color || TAG_COLORS.default.bg, text: '#fff' } });
    });
  }
  return tags;
};

const FollowUpsTable = ({ data = [], selectedRowKeys = [], onSelectionChange }) => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('dueToday');

  const heroFilter = FILTERS[0];
  const chipFilters = FILTERS.slice(1);

  const columns = [
    {
      title: (
        <span className="fu-col-title">
          FOLLOW UP <CaretDownOutlined style={{ fontSize: 11 }} />
        </span>
      ),
      dataIndex: 'followUp',
      key: 'followUp',
      width: 180,
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (text) => <span className="fu-followup-text">{text || '—'}</span>,
    },
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (text, record) => (
        <span
          className="fu-name"
          onClick={() => navigate(`${ClientDetailRoute}/${record.key}`)}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'DETAILS',
      key: 'details',
      onCell: () => ({ style: { whiteSpace: 'nowrap', maxWidth: 600, overflow: 'hidden' } }),
      render: (_, record) => {
        const tags = buildTags(record);
        const visibleTags = tags.slice(0, 2);
        const extraCount = tags.length - visibleTags.length;
        return (
          <div className="fu-details-row">
            {record.assignedTo && record.assignedTo !== '—' && (
              <div className="fu-assignee-badge">
                <span>↗</span>
                <span className="fu-assignee-name">{record.assignedTo}</span>
              </div>
            )}
            {visibleTags.map((t, i) => <Tag key={i} label={t.label} color={t.color} />)}
            {extraCount > 0 && <span className="fu-more-badge">+{extraCount} more</span>}
            {record.notes && record.notes !== '-' && record.notes !== '—' && (
              <span className="fu-notes-text">{record.notes}</span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="fu-wrap">
      {/* Filter Banner */}
      <div className="fu-banner">
        <div
          className={`fu-hero${activeFilter === heroFilter.key ? ' fu-hero--active' : ''}`}
          onClick={() => setActiveFilter(heroFilter.key)}
        >
          <CalendarOutlined className="fu-hero-icon" />
          <span className="fu-hero-label">{heroFilter.label}</span>
          <span className="fu-hero-count">({heroFilter.count})</span>
        </div>

        <div className="fu-chips">
          {chipFilters.map(f => (
            <div
              key={f.key}
              className={`fu-chip${activeFilter === f.key ? ' fu-chip--active' : ''}${f.variant ? ` fu-chip--${f.variant}` : ''}`}
              onClick={() => setActiveFilter(f.key)}
              title={f.label}
            >
              <CalendarOutlined className="fu-chip-icon" />
              <span className="fu-chip-count">{f.count}</span>
            </div>
          ))}
        </div>
      </div>

      <CommonTable
        columns={columns}
        dataSource={data}
        scroll={{ x: 'max-content' }}
        rowSelection={{
          selectedRowKeys,
          onChange: onSelectionChange,
          getCheckboxProps: () => ({ className: 'custom-checkbox' }),
        }}
      />
    </div>
  );
};

export default FollowUpsTable;
